'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Product {
  id: string;
  name: string;
  storage: string;
  colors: string;
  fullPrice: string;
  upfront: string;
  monthly: string;
  badge?: string;
  image: string;
  alt: string;
}

interface Step {
  num: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  hasArrow: boolean;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const products: Product[] = [
{
  id: 'iphone-xr',
  name: 'iPhone XR',
  storage: '64GB',
  colors: 'Available in multiple colors',
  fullPrice: 'R4,000',
  upfront: 'R800',
  monthly: '≈ R1,067/mo',
  image: 'https://appleroom.store/assets/images/0821ec1ca65aeda897e7ad4672e8c8d5-1784180656751.jpg',
  alt: 'iPhone XR front view showing colorful display'
},
{
  id: 'iphone-11',
  name: 'iPhone 11',
  storage: '128GB',
  colors: 'Black, White, Green, Purple',
  fullPrice: 'R5,500',
  upfront: 'R1,100',
  monthly: '≈ R1,467/mo',
  badge: 'Popular',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_126862a05-1770137380793.png',
  alt: 'iPhone 11 front view with bright display'
},
{
  id: 'iphone-11-pro',
  name: 'iPhone 11 Pro',
  storage: '128GB',
  colors: 'Midnight Green, Space Grey',
  fullPrice: 'R6,300',
  upfront: 'R1,260',
  monthly: '≈ R1,680/mo',
  image: 'https://images.unsplash.com/photo-1610284120508-2bc1905ec669',
  alt: 'iPhone 11 Pro front view in midnight green'
},
{
  id: 'iphone-13',
  name: 'iPhone 13',
  storage: '128GB',
  colors: 'Midnight, Starlight, Pink, Blue',
  fullPrice: 'R7,000',
  upfront: 'R1,400',
  monthly: '≈ R1,867/mo',
  badge: 'Best Value',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c95f6d14-1775058099616.png',
  alt: 'iPhone 13 front view in midnight color with notch'
},
{
  id: 'iphone-13-pro-max',
  name: 'iPhone 13 Pro Max',
  storage: '128GB',
  colors: 'Sierra Blue, Gold, Graphite',
  fullPrice: 'R8,000',
  upfront: 'R1,600',
  monthly: '≈ R2,133/mo',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_171571d0d-1772455314835.png',
  alt: 'iPhone 13 Pro Max front view in sierra blue'
},
{
  id: 'iphone-14-pro',
  name: 'iPhone 14 Pro',
  storage: '128GB',
  colors: 'Deep Purple, Space Black, Silver',
  fullPrice: 'R9,300',
  upfront: 'R1,860',
  monthly: '≈ R2,480/mo',
  badge: 'Dynamic Island',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17e26b727-1772158955332.png',
  alt: 'iPhone 14 Pro front view in deep purple with Dynamic Island'
},
{
  id: 'iphone-15-pro-max',
  name: 'iPhone 15 Pro Max',
  storage: '128GB',
  colors: 'Black Titanium, Natural Titanium',
  fullPrice: 'R10,500',
  upfront: 'R2,100',
  monthly: '≈ R2,800/mo',
  badge: 'Titanium',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17b82fb7a-1772960574407.png',
  alt: 'iPhone 15 Pro Max front view in black titanium finish'
},
{
  id: 'iphone-16',
  name: 'iPhone 16',
  storage: '256GB',
  colors: 'Ultramarine, Teal, Pink, White, Black',
  fullPrice: 'R13,000',
  upfront: 'R2,600',
  monthly: '≈ R3,467/mo',
  badge: 'New',
  image: 'https://appleroom.store/assets/images/ffbf63691a5eb79d217105e82c648981-1784181076428.jpg',
  alt: 'iPhone 16 front view in ultramarine with camera control button'
},
{
  id: 'iphone-17',
  name: 'iPhone 17',
  storage: '256GB',
  colors: 'Desert Titanium, Sky Blue, Black',
  fullPrice: 'R15,000',
  upfront: 'R3,000',
  monthly: '≈ R4,000/mo',
  badge: 'Latest 2025',
  image: 'https://appleroom.store/assets/images/79ea807900a1cdc58aded73674714a96-1784181175057.jpg',
  alt: 'iPhone 17 front view in desert titanium with latest design'
}];


// ─── SVG Icons ────────────────────────────────────────────────────────────────
const ArrowIcon = ({ size = 16 }: {size?: number;}) =>
<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>;


const MonitorIcon = () =>
<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>;


const BoxIcon = () =>
<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
  </svg>;


const CardIcon = () =>
<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </svg>;


const DeliveryIcon = () =>
<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>;


const InfoIcon = () =>
<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4M12 16h.01" />
  </svg>;


const WhatsAppIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="28" height="28" fill="white" aria-hidden="true">
    <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.47 2.027 7.773L0 32l8.418-2.007A15.93 15.93 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.77-1.853l-.485-.289-5.002 1.193 1.215-4.868-.317-.5A13.267 13.267 0 0 1 2.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.27-9.87c-.398-.199-2.355-1.162-2.72-1.295-.365-.133-.63-.199-.896.199-.265.398-1.029 1.295-1.261 1.56-.232.265-.465.298-.863.1-.398-.199-1.681-.619-3.202-1.977-1.183-1.056-1.982-2.36-2.214-2.758-.232-.398-.025-.613.174-.811.179-.178.398-.465.597-.697.199-.232.265-.398.398-.664.133-.265.066-.497-.033-.697-.1-.199-.896-2.16-1.228-2.957-.323-.776-.651-.671-.896-.683l-.763-.013c-.265 0-.697.1-1.062.497-.365.398-1.394 1.362-1.394 3.322s1.427 3.854 1.626 4.12c.199.265 2.808 4.287 6.803 6.014.951.41 1.693.655 2.271.839.954.304 1.823.261 2.51.158.766-.114 2.355-.963 2.688-1.893.332-.93.332-1.727.232-1.893-.099-.166-.365-.265-.763-.464z" />
  </svg>;


// ─── Header Component ─────────────────────────────────────────────────────────
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b shadow-sm"
      style={{
        backgroundColor: '#ffffff',
        borderBottomColor: 'rgba(113, 63, 18, 0.4)'
      }}>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5">
            <Image
              src="/assets/images/IMG_20260716_072624-1785031428263.png"
              alt="AppleRoom logo"
              width={52}
              height={52}
              className="rounded-lg object-cover flex-shrink-0"
              style={{ width: '52px', height: '52px' }} />
            
            <span
              className="font-sans tracking-tight text-[30px] font-extrabold"
              style={{ color: '#B8860B' }}>
              
              AppleRoom
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {['Store', 'Order', 'My Profile', 'Management'].map((item) =>
            <Link
              key={item}
              href={item === 'Store' ? '/' : item === 'Order' ? 'https://appleroom.store/checkout' : item === 'My Profile' ? '/user-profile' : '/management'}
              className="text-sm font-medium text-black hover:text-yellow-700 transition-colors">
              
                {item}
              </Link>
            )}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link
              href="https://appleroom.store/checkout"
              className="btn-gold px-5 py-2.5 rounded-full text-sm font-bold inline-flex items-center gap-2">
              
              Order Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2"
            aria-label="Open menu"
            onClick={() => setMenuOpen(!menuOpen)}>
            
            <span
              className={`block w-6 h-0.5 bg-yellow-600 transition-all duration-300 ${
              menuOpen ? 'rotate-45 translate-y-2' : ''}`
              } />
            
            <span
              className={`block w-6 h-0.5 bg-yellow-600 transition-all duration-300 ${
              menuOpen ? 'opacity-0' : ''}`
              } />
            
            <span
              className={`block w-6 h-0.5 bg-yellow-600 transition-all duration-300 ${
              menuOpen ? '-rotate-45 -translate-y-2' : ''}`
              } />
            
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen &&
        <div className="md:hidden pb-4 border-t" style={{ borderColor: 'rgba(113, 63, 18, 0.2)' }}>
            <nav className="flex flex-col gap-3 pt-4">
              {['Store', 'Order', 'My Profile', 'Management'].map((item) =>
            <Link
              key={item}
              href={item === 'Store' ? '/' : item === 'Order' ? 'https://appleroom.store/checkout' : item === 'My Profile' ? '/user-profile' : '/management'}
              className="text-sm font-medium text-black hover:text-yellow-700 transition-colors px-2"
              onClick={() => setMenuOpen(false)}>
              
                  {item}
                </Link>
            )}
              <Link
              href="https://appleroom.store/checkout"
              className="btn-gold px-5 py-2.5 rounded-full text-sm font-bold inline-flex items-center gap-2 w-fit mt-2"
              onClick={() => setMenuOpen(false)}>
              
                Order Now
              </Link>
            </nav>
          </div>
        }
      </div>
    </header>);

}

// ─── Hero Section ─────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section className="relative flex flex-col items-center justify-center overflow-hidden pt-24 pb-10">
      {/* Backgrounds */}
      <div className="absolute inset-0 grid-bg-subtle pointer-events-none" />
      <div className="absolute inset-0 hero-glow-bg pointer-events-none" />
      <div className="atmospheric-blob-1 absolute top-1/4 left-1/4 w-96 h-96 rounded-full pointer-events-none" />
      <div className="atmospheric-blob-2 absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full pointer-events-none" />
      <div className="noise-overlay absolute inset-0" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-4 sm:px-6 max-w-4xl mx-auto w-full">
        {/* Badge */}
        <div className="mb-4">
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-widest"
            style={{
              borderColor: 'rgba(201, 168, 76, 0.3)',
              backgroundColor: 'rgba(201, 168, 76, 0.05)',
              color: 'var(--primary)'
            }}>
            
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse-gold"
              style={{ backgroundColor: 'var(--primary)' }} />
            
            Official iPhone Store
          </span>
        </div>

        {/* Heading */}
        <h1 className="mb-3 leading-tight">
          <span className="block text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight" style={{ color: 'var(--foreground)' }}>
            Apple<span style={{ color: '#C9A84C' }}>Room</span>
          </span>
          <span className="block text-xl sm:text-2xl font-medium mt-1" style={{ color: 'var(--muted-foreground)' }}>
            Own an iPhone Today —{' '}
            <span className="gold-text-gradient font-bold">From Just R800</span>
          </span>
        </h1>

        {/* Description */}
        <p className="text-sm sm:text-base max-w-2xl leading-relaxed mb-2 font-medium" style={{ color: 'var(--muted-foreground)' }}>
          AppleRoom makes iPhones accessible through our easy installment plan. Pay just{' '}
          <strong style={{ color: 'var(--foreground)' }}>20% upfront</strong> and the remaining balance in{' '}
          <strong style={{ color: 'var(--foreground)' }}>3 monthly installments</strong> as you enjoy your device.
        </p>
        <p className="text-xs max-w-lg leading-relaxed mb-6" style={{ color: 'rgba(136,136,136,0.7)' }}>
          Same-day courier delivery available. Order before 2PM for delivery today. Banking details provided at checkout — payment confirms your order instantly.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 items-center w-full sm:w-auto">
          <a
            href="#products"
            className="btn-gold px-6 py-3 rounded-full text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center">
            
            Browse iPhones
            <ArrowIcon size={16} />
          </a>
          <Link
            href="https://appleroom.store/checkout"
            className="btn-outline-gold px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center">
            
            Order Now
          </Link>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-3 gap-4 sm:gap-8 w-full max-w-sm">
          {[
          { value: '20%', label: 'Upfront Only' },
          { value: '3x', label: 'Easy Installments' },
          { value: 'Same Day', label: 'Courier Delivery' }].
          map(({ value, label }) =>
          <div key={label} className="flex flex-col items-center gap-1">
              <span className="text-lg sm:text-xl font-extrabold gold-text-gradient">{value}</span>
              <span className="text-xs font-medium text-center leading-tight" style={{ color: 'var(--muted-foreground)' }}>
                {label}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
        style={{ background: 'linear-gradient(to top, var(--background), transparent)' }} />
      
    </section>);

}

// ─── Notice Banner ────────────────────────────────────────────────────────────
function NoticeBanner() {
  return (
    <section className="relative z-10 py-3 px-4">
      <div className="max-w-4xl mx-auto">
        <div
          className="relative overflow-hidden rounded-2xl px-4 py-3"
          style={{
            border: '1px solid rgba(201, 168, 76, 0.3)',
            backgroundColor: 'rgba(201, 168, 76, 0.05)'
          }}>
          
          {/* Shimmer */}
          <div className="shimmer-effect absolute inset-0 pointer-events-none rounded-2xl" />

          <div className="relative flex items-start gap-3">
            <div
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5"
              style={{ backgroundColor: 'rgba(201, 168, 76, 0.15)', color: 'var(--primary)' }}>
              
              <InfoIcon />
            </div>
            <div>
              <p className="text-sm font-bold mb-0.5" style={{ color: 'var(--primary)' }}>
                Product Availability Notice
              </p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Only the iPhones listed below are currently available in stock. Keep visiting our website for more products as we expand our range. All prices shown are the{' '}
                <strong style={{ color: 'var(--foreground)' }}>20% upfront payment</strong> — full device price shown on each product card.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

// ─── Product Card ─────────────────────────────────────────────────────────────
function ProductCard({ product }: {product: Product;}) {
  return (
    <div className="card-glow rounded-2xl overflow-hidden flex flex-col group" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}>
      {/* Image */}
      <div className="relative overflow-hidden rounded-t-2xl" style={{ height: '200px' }}>
        <div className="relative h-full w-full">
          <img
            src={product.image}
            alt={product.alt}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="eager"
            decoding="async" />
          
        </div>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />

        {/* Badge */}
        {product.badge &&
        <div className="absolute top-3 left-3">
            <span className="product-badge px-2 py-0.5 rounded-full text-xs">
              {product.badge}
            </span>
          </div>
        }

        {/* Price tag */}
        <div className="absolute bottom-3 right-3">
          <div
            className="backdrop-blur-sm rounded-xl px-2.5 py-1.5 text-right"
            style={{
              backgroundColor: 'rgba(0,0,0,0.6)',
              border: '1px solid rgba(250, 204, 21, 0.5)'
            }}>
            
            <div className="price-tag gold-text-gradient text-base">{product.upfront}</div>
            <div className="text-xs font-medium" style={{ color: 'rgb(254, 240, 138)' }}>upfront today</div>
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="flex flex-col flex-1 p-4">
        <h3 className="text-base font-extrabold mb-0.5" style={{ color: 'var(--foreground)' }}>
          {product.name}
        </h3>
        <p className="text-xs font-medium mb-3" style={{ color: 'var(--muted-foreground)' }}>
          {product.storage} · {product.colors}
        </p>

        {/* Pricing breakdown */}
        <div
          className="rounded-xl p-3 mb-3"
          style={{
            backgroundColor: 'rgba(42,42,42,0.5)',
            border: '1px solid var(--border)'
          }}>
          
          <div
            className="flex justify-between items-center mb-2 pb-2"
            style={{ borderBottom: '1px solid var(--border)' }}>
            
            <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>Full device price</span>
            <span className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>{product.fullPrice}</span>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Pay today (20%)</span>
              <span className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>{product.upfront}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>3 × monthly</span>
              <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{product.monthly}</span>
            </div>
          </div>
        </div>

        {/* Order button */}
        <Link
          href={`/order-confirmation?product=${product.id}`}
          className="btn-gold w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 mt-auto">
          
          Order Now
          <ArrowIcon size={14} />
        </Link>
      </div>
    </div>);

}

// ─── Products Section ─────────────────────────────────────────────────────────
function ProductsSection() {
  return (
    <section id="products" className="relative py-8 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 grid-bg-subtle pointer-events-none opacity-50" />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-widest mb-3"
            style={{
              borderColor: 'rgba(201, 168, 76, 0.3)',
              backgroundColor: 'rgba(201, 168, 76, 0.05)',
              color: 'var(--primary)'
            }}>
            
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--primary)' }} />
            Available Now
          </span>
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-2" style={{ color: 'var(--foreground)' }}>
            Choose Your <span className="gold-text-gradient">iPhone</span>
          </h2>
          <p className="max-w-xl mx-auto text-xs sm:text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            All prices shown are the <strong style={{ color: 'var(--foreground)' }}>20% upfront payment</strong>. Full device price and installment breakdown shown on each card.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) =>
          <ProductCard key={product.id} product={product} />
          )}
        </div>
      </div>
    </section>);

}

// ─── How It Works Section ─────────────────────────────────────────────────────
function HowItWorksSection() {
  const steps: Step[] = [
  {
    num: '01',
    title: 'Choose Your iPhone',
    description: 'Browse our available models and select the iPhone that suits you. All prices shown are the 20% upfront amount.',
    icon: <MonitorIcon />,
    hasArrow: true
  },
  {
    num: '02',
    title: 'Place Your Order',
    description: 'Complete the order form with your details and preferred courier. Choose between Pep/Paxi, Courier Guy, or Takealot delivery.',
    icon: <BoxIcon />,
    hasArrow: true
  },
  {
    num: '03',
    title: 'Pay 20% Upfront',
    description: 'Make your upfront payment using our banking details. Payment confirmation triggers same-day dispatch of your order.',
    icon: <CardIcon />,
    hasArrow: true
  },
  {
    num: '04',
    title: 'Receive & Pay Installments',
    description: 'Get your iPhone via same-day courier. Pay the remaining 80% in 3 equal monthly installments as you use your device.',
    icon: <DeliveryIcon />,
    hasArrow: false
  }];


  return (
    <section className="relative py-8 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: 'rgba(26,26,26,0.3)' }} />
      <div className="atmospheric-blob-1 absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none opacity-50" />

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-widest mb-3"
            style={{
              borderColor: 'rgba(201, 168, 76, 0.3)',
              backgroundColor: 'rgba(201, 168, 76, 0.05)',
              color: 'var(--primary)'
            }}>
            
            Simple Process
          </span>
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-2" style={{ color: 'var(--foreground)' }}>
            How AppleRoom <span className="gold-text-gradient">Works</span>
          </h2>
          <p className="max-w-xl mx-auto text-xs sm:text-sm" style={{ color: 'var(--muted-foreground)' }}>
            Get your iPhone in 4 simple steps. No credit checks, no complicated paperwork.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((step) =>
          <div
            key={step.num}
            className="relative flex flex-col items-start rounded-2xl p-4 transition-all duration-300"
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid rgba(42,42,42,0.5)'
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(201, 168, 76, 0.3)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(42,42,42,0.5)';
            }}>
            
              <div className="flex items-center gap-2 mb-3">
                <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: 'rgba(201, 168, 76, 0.1)',
                  border: '1px solid rgba(201, 168, 76, 0.2)',
                  color: 'var(--primary)'
                }}>
                
                  {step.icon}
                </div>
                <span
                className="text-2xl font-extrabold leading-none"
                style={{ color: 'rgba(201, 168, 76, 0.2)' }}>
                
                  {step.num}
                </span>
              </div>
              <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--foreground)' }}>
                {step.title}
              </h3>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                {step.description}
              </p>

              {/* Arrow connector (desktop only) */}
              {step.hasArrow &&
            <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                  <div
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--background)',
                  border: '1px solid var(--border)'
                }}>
                
                    <ArrowIcon size={10} />
                  </div>
                </div>
            }
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="text-center mt-6">
          <Link
            href="https://appleroom.store/checkout"
            className="btn-gold px-8 py-3 rounded-full text-sm font-bold inline-flex items-center gap-2">
            
            Start Your Order
            <ArrowIcon size={16} />
          </Link>
        </div>
      </div>
    </section>);

}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer
      className="pt-8 pb-6"
      style={{
        borderTop: '1px solid var(--border)',
        backgroundColor: 'var(--background)'
      }}>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Brand */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Image
                src="/assets/images/IMG_20260716_072624-1785031428263.png"
                alt="AppleRoom Logo"
                width={28}
                height={28}
                className="flex-shrink-0 rounded" />
              
              <span className="font-sans font-bold text-sm tracking-tight" style={{ color: 'var(--foreground)' }}>
                AppleRoom
              </span>
            </div>
            <p className="text-xs max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
              Own an iPhone today. Pay 20% upfront, rest in 3 installments.
            </p>
          </div>

          {/* Nav */}
          <nav className="flex flex-wrap gap-x-6 gap-y-2">
            {[
            { label: 'Store', href: '/' },
            { label: 'Order', href: 'https://appleroom.store/checkout' },
            { label: 'Profile', href: '/user-profile' }].
            map(({ label, href }) =>
            <Link
              key={label}
              href={href}
              className="text-xs font-medium transition-colors hover:text-white"
              style={{ color: 'var(--muted-foreground)' }}>
              
                {label}
              </Link>
            )}
          </nav>
        </div>

        {/* Bottom bar */}
        <div
          className="mt-6 pt-4 flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ borderTop: '1px solid var(--border)' }}>
          
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            © 2026 AppleRoom iPhone Store. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Privacy Policy</span>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>);

}

// ─── WhatsApp Button ──────────────────────────────────────────────────────────
function WhatsAppButton() {
  return (
    <a
      href="https://wa.me/27000000000?text=Hi%20AppleRoom!%20I%20would%20like%20to%20enquire%20about%20an%20iPhone%20order."
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 group">
      
      <span
        className="bg-white text-gray-800 text-sm font-semibold px-3 py-1.5 rounded-full shadow-lg border border-gray-100 whitespace-nowrap transition-all duration-300 opacity-0 translate-x-2 pointer-events-none group-hover:opacity-100 group-hover:translate-x-0">
        
        Chat with us
      </span>
      <div
        className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110"
        style={{ backgroundColor: '#25D366' }}>
        
        <span
          className="absolute inset-0 rounded-full animate-ping opacity-30"
          style={{ backgroundColor: '#25D366' }} />
        
        <WhatsAppIcon />
      </div>
    </a>);

}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  return (
    <main
      className="min-h-screen overflow-x-hidden"
      style={{ backgroundColor: 'var(--background)' }}>
      
      <Header />
      <HeroSection />
      <NoticeBanner />
      <ProductsSection />
      <HowItWorksSection />
      <Footer />
      <WhatsAppButton />
    </main>);

}